import { createContext, useContext, useEffect, useState, useCallback } from "react";
import en from "./en.json";
import ar from "./ar.json";
import { ensureArabicFonts } from "@/lib/translator";

const DICTS = { en, ar };
const I18nContext = createContext(null);

function getNested(obj, path) {
    return path.split(".").reduce((o, k) => (o && o[k] !== undefined ? o[k] : null), obj);
}

export function I18nProvider({ children }) {
    const [lang, setLang] = useState(() => localStorage.getItem("tr_lang") || "en");

    useEffect(() => {
        localStorage.setItem("tr_lang", lang);
        if (lang === "ar") ensureArabicFonts();
        // The LLM page-translator (lib/translator.js) owns the <html> attrs when
        // a non-English site language is active — don't stomp its rtl/lang.
        const appLang = localStorage.getItem("app_lang") || "en";
        if (appLang === "en") {
            document.documentElement.lang = lang;
            document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
        }
    }, [lang]);

    const t = useCallback(
        (key, fallback = "") => {
            const dict = DICTS[lang] || DICTS.en;
            const val = getNested(dict, key);
            if (val != null) return val;
            return getNested(DICTS.en, key) ?? fallback ?? key;
        },
        [lang],
    );

    const toggle = useCallback(() => setLang((l) => (l === "en" ? "ar" : "en")), []);

    return <I18nContext.Provider value={{ lang, setLang, toggle, t, dir: lang === "ar" ? "rtl" : "ltr" }}>{children}</I18nContext.Provider>;
}

export function useI18n() {
    const ctx = useContext(I18nContext);
    if (!ctx) throw new Error("useI18n outside provider");
    return ctx;
}
